import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../../stores/authStore';
import { UserRole } from '../../types/auth';

interface NavItem {
	icon: string;
	label: string;
	path: string;
	roles: UserRole[];
}

interface SidebarProps {
	isOpen: boolean;
	onClose: () => void;
}

const navigationItems: NavItem[] = [
	{ icon: '🏠', label: 'Dashboard', path: '/dashboard', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'STAFF'] },
	{ icon: '📅', label: 'Bookings', path: '/bookings', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'STAFF'] },
	{ icon: '👥', label: 'Providers', path: '/providers', roles: ['SUPER_ADMIN'] },
	{ icon: '��', label: 'Services', path: '/services', roles: ['SALON_OWNER', 'MANAGER'] },
	{ icon: '🧾', label: 'TCR', path: '/tcr', roles: ['STAFF', 'SALON_OWNER', 'MANAGER'] },
	{ icon: '✅', label: 'Approvals', path: '/approvals', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER'] },
	{ icon: '📦', label: 'Inventory', path: '/inventory', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER'] },
	{ icon: '💰', label: 'Commissions', path: '/commissions', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER', 'STAFF'] },
	{ icon: '⚙️', label: 'Settings', path: '/settings', roles: ['SUPER_ADMIN', 'SALON_OWNER', 'MANAGER'] },
];

const Sidebar = ({ isOpen, onClose }: SidebarProps) => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useAuthStore();

	const filteredNav = navigationItems.filter(item => user && item.roles.includes(user.role));

	const handleNavigation = (path: string) => {
		navigate(path);
		onClose(); // Close sidebar on mobile after navigation
	};

	return (
		<div
			className={`
				bg-white dark:bg-gray-800
				border-r border-gray-200 dark:border-gray-700
				text-gray-900 dark:text-gray-100
				py-7 flex flex-col gap-4.5
				transition-all duration-300 ease-in-out
				${isOpen ? 'w-[260px] px-5' : 'w-[80px] px-3'}
			`}
		>
			{/* Header */}
			<div className="flex items-center justify-between overflow-hidden">
				{isOpen ? (
					<>
						<div className="font-bold text-xl tracking-wide whitespace-nowrap">Beauty Business Portal</div>
						<button
							onClick={onClose}
							className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex-shrink-0"
							aria-label="Collapse sidebar"
						>
							<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
							</svg>
						</button>
					</>
				) : (
					<button
						onClick={onClose}
						className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors mx-auto"
						aria-label="Expand sidebar"
						title="Expand sidebar"
					>
						<svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
					</button>
				)}
			</div>
			<div className="h-2.5"></div>

			{/* Navigation Items */}
			<div className="flex flex-col gap-2">
				{filteredNav.map(item => (
					<div
						key={item.path}
						className={`p-2.5 rounded-lg flex gap-2.5 items-center cursor-pointer transition-all ${
							location.pathname === item.path
								? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium'
								: 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
						} ${!isOpen && 'justify-center'}`}
						onClick={() => handleNavigation(item.path)}
						title={!isOpen ? item.label : undefined}
					>
						<span className="text-xl">{item.icon}</span>
						{isOpen && <span className="whitespace-nowrap">{item.label}</span>}
					</div>
				))}
			</div>

			<div className="flex-1"></div>

			{/* User Info */}
			{user && isOpen && (
				<div className="text-sm border-t border-gray-200 dark:border-gray-700 pt-4">
					<div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Logged in as:</div>
					<div className="font-semibold text-gray-900 dark:text-gray-100 truncate">{user.email}</div>
					{user.businessName && <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">{user.businessName}</div>}
				</div>
			)}
			{user && !isOpen && (
				<div className="flex justify-center border-t border-gray-200 dark:border-gray-700 pt-4">
					<div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold" title={user.email}>
						{user.email.charAt(0).toUpperCase()}
					</div>
				</div>
			)}
		</div>
	);
};

export default Sidebar;
